<script lang="ts">
  import type { JumpMarker } from "@piano/shared";
  import { drawStroke, drawStrokes, type DraftStroke, type Point } from "../../lib/viewer/ink.js";
  import type { InkStore } from "../../lib/viewer/ink-store.svelte.js";
  import { renderPage, type PDFDocumentProxy, type RenderHandle } from "../../lib/viewer/pdf.js";

  type Role = "source" | "target";

  let {
    pdf,
    pageNumber,
    width,
    aspect,
    ink,
    inkActive = false,
    penOnly = true,
    markers = [],
    markersEditing = false,
    placing = false,
    highlightId = null,
    eager = false,
    renderDelay = 0,
    onmarker,
    onplace,
    onmove,
  }: {
    pdf: PDFDocumentProxy;
    pageNumber: number;
    width: number;
    aspect: number;
    ink: InkStore;
    inkActive?: boolean;
    penOnly?: boolean;
    markers?: JumpMarker[];
    markersEditing?: boolean;
    placing?: boolean;
    highlightId?: string | null;
    eager?: boolean;
    /** Wartezeit vor dem ersten Rendern (ms) – für Seiten, die nur vorab bereitliegen */
    renderDelay?: number;
    onmarker?: (marker: JumpMarker, role: Role) => void;
    onplace?: (pageNumber: number, x: number, y: number) => void;
    onmove?: (marker: JumpMarker, role: Role, x: number, y: number) => void;
  } = $props();

  let host: HTMLDivElement | undefined = $state();
  let pdfCanvas: HTMLCanvasElement | undefined = $state();
  let inkCanvas: HTMLCanvasElement | undefined = $state();
  let near = $state(false);
  let rendered = $state(false);
  let renderedWidth = 0;
  let task: RenderHandle | null = null;

  const height = $derived(Math.round(width * aspect));

  // Nur Seiten in Sichtweite rendern; weit entfernte geben ihren Speicher frei.
  $effect(() => {
    if (!host) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        near = Boolean(entry?.isIntersecting);
      },
      { rootMargin: "150% 0px 150% 0px" },
    );
    observer.observe(host);
    return () => observer.disconnect();
  });

  $effect(() => {
    const visible = near || eager;
    const w = width;
    if (!pdfCanvas) return;
    if (!visible) {
      task?.cancel();
      task = null;
      if (rendered) {
        pdfCanvas.width = pdfCanvas.height = 0;
        rendered = false;
        renderedWidth = 0;
      }
      return;
    }
    if (rendered && Math.abs(renderedWidth - w) < 2) return;
    // Beim Zoomen kurz warten, bis die Größe steht – das alte Bild wird solange skaliert.
    const delay = rendered ? 180 : renderDelay;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const page = await pdf.getPage(pageNumber);
      if (cancelled || !pdfCanvas) return;
      task?.cancel();
      task = renderPage(page, pdfCanvas, w);
      try {
        await task.promise;
        if (!cancelled) {
          rendered = true;
          renderedWidth = w;
        }
      } catch {
        // abgebrochen (Zoom, Wegscrollen) – nächster Durchlauf rendert neu
      }
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  });

  $effect(() => () => task?.cancel());

  // --- Notizen ---------------------------------------------------------------

  const inkScale = $derived(Math.min(window.devicePixelRatio || 1, 2));
  let gestures = new Map<number, { stroke: DraftStroke; drawn: number }>();

  // Neu zeichnen bei Änderung der Striche oder der Größe
  $effect(() => {
    void ink.revisions[pageNumber];
    const show = near || eager;
    if (!inkCanvas) return;
    if (!show || (!ink.hasInk(pageNumber) && !gestures.size)) {
      inkCanvas.width = inkCanvas.height = 0;
      return;
    }
    const w = Math.round(width * inkScale);
    const h = Math.round(height * inkScale);
    if (inkCanvas.width !== w || inkCanvas.height !== h) {
      inkCanvas.width = w;
      inkCanvas.height = h;
    }
    const ctx = inkCanvas.getContext("2d");
    if (ctx) drawStrokes(ctx, ink.strokes(pageNumber));
  });

  function ensureInkCanvas(): CanvasRenderingContext2D | null {
    if (!inkCanvas) return null;
    const w = Math.round(width * inkScale);
    const h = Math.round(height * inkScale);
    if (inkCanvas.width !== w || inkCanvas.height !== h) {
      inkCanvas.width = w;
      inkCanvas.height = h;
      const ctx = inkCanvas.getContext("2d");
      if (ctx) drawStrokes(ctx, ink.strokes(pageNumber));
    }
    return inkCanvas.getContext("2d");
  }

  function accepts(event: PointerEvent): boolean {
    if (!inkActive) return false;
    if (event.pointerType === "mouse") return event.button === 0;
    return !penOnly || event.pointerType === "pen";
  }

  function point(event: PointerEvent): Point {
    const rect = inkCanvas!.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / Math.max(1, rect.width)));
    const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / Math.max(1, rect.height)));
    const p = event.pointerType === "pen" ? Math.max(0.05, Math.min(1, event.pressure || 0.5)) : 0.8;
    return [x, y, p];
  }

  function down(event: PointerEvent): void {
    if (!accepts(event) || gestures.has(event.pointerId)) return;
    event.preventDefault();
    inkCanvas!.setPointerCapture(event.pointerId);
    const stroke = ink.begin(pageNumber, point(event), width, event.pointerType === "pen");
    gestures.set(event.pointerId, { stroke, drawn: 0 });
    const ctx = ensureInkCanvas();
    if (ctx) drawStroke(ctx, stroke);
  }

  function move(event: PointerEvent): void {
    const gesture = gestures.get(event.pointerId);
    if (!gesture) return;
    event.preventDefault();
    const events = event.getCoalescedEvents?.() ?? [event];
    if (!ink.extend(gesture.stroke, (events.length ? events : [event]).map(point))) return;
    const ctx = ensureInkCanvas();
    if (ctx) drawStroke(ctx, gesture.stroke, Math.max(1, gesture.drawn));
    gesture.drawn = gesture.stroke.points.length - 1;
  }

  function up(event: PointerEvent, keep: boolean): void {
    const gesture = gestures.get(event.pointerId);
    if (!gesture) return;
    gestures.delete(event.pointerId);
    if (keep) ink.commit(pageNumber, gesture.stroke);
    else ink.revisions[pageNumber] = (ink.revisions[pageNumber] ?? 0) + 1;
  }

  // iPad: Stift darf nicht scrollen; im „Stift + Finger“-Modus scrollt gar nichts.
  function touchGuard(event: TouchEvent): void {
    if (!inkActive) return;
    if (!penOnly) return event.preventDefault();
    for (const touch of Array.from(event.touches)) {
      if ((touch as Touch & { touchType?: string }).touchType === "stylus") return event.preventDefault();
    }
  }

  $effect(() => {
    const canvas = inkCanvas;
    if (!canvas) return;
    canvas.addEventListener("touchstart", touchGuard, { passive: false });
    canvas.addEventListener("touchmove", touchGuard, { passive: false });
    return () => {
      canvas.removeEventListener("touchstart", touchGuard);
      canvas.removeEventListener("touchmove", touchGuard);
    };
  });

  // --- Sprungmarken ------------------------------------------------------------

  interface Pin {
    marker: JumpMarker;
    role: Role;
    x: number;
    y: number;
  }
  const pins = $derived(
    markers.flatMap((marker) =>
      (["source", "target"] as const)
        .filter((role) => marker[role]?.pageNumber === pageNumber)
        .map((role) => ({ marker, role, x: marker[role]!.x, y: marker[role]!.y }) satisfies Pin),
    ),
  );

  let drag = $state<{
    id: string;
    role: Role;
    x: number;
    y: number;
    moved: boolean;
    pointerId: number;
  } | null>(null);

  function pinDown(event: PointerEvent, pin: Pin): void {
    event.stopPropagation();
    if (!markersEditing) return;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    drag = {
      id: pin.marker.id,
      role: pin.role,
      x: pin.x,
      y: pin.y,
      moved: false,
      pointerId: event.pointerId,
    };
  }
  function pinMove(event: PointerEvent): void {
    if (!drag || drag.pointerId !== event.pointerId || !host) return;
    const rect = host.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
    if (!drag.moved && Math.hypot(x - drag.x, y - drag.y) < 1) return;
    drag = { ...drag, x, y, moved: true };
  }
  function pinUp(event: PointerEvent, pin: Pin): void {
    event.stopPropagation();
    const current = drag;
    drag = null;
    if (current?.moved) onmove?.(pin.marker, pin.role, current.x, current.y);
    else onmarker?.(pin.marker, pin.role);
  }

  function placeClick(event: MouseEvent): void {
    if (!placing || !host) return;
    event.stopPropagation();
    const rect = host.getBoundingClientRect();
    onplace?.(
      pageNumber,
      ((event.clientX - rect.left) / rect.width) * 100,
      ((event.clientY - rect.top) / rect.height) * 100,
    );
  }
</script>

<div
  bind:this={host}
  class="page"
  class:placing
  data-page={pageNumber}
  style:width="{width}px"
  style:height="{height}px"
  onclick={placeClick}
  role="presentation"
>
  <canvas bind:this={pdfCanvas} class="pdf" class:ready={rendered} aria-label="Seite {pageNumber}"></canvas>
  {#if !rendered}<span class="loading" aria-hidden="true"></span>{/if}
  <canvas
    bind:this={inkCanvas}
    class="ink"
    class:active={inkActive}
    class:free={inkActive && !penOnly}
    onpointerdown={down}
    onpointermove={move}
    onpointerup={(e) => up(e, true)}
    onpointercancel={(e) => up(e, false)}
    oncontextmenu={(e) => inkActive && e.preventDefault()}
  ></canvas>
  {#each pins as pin (pin.marker.id + pin.role)}
    {@const dragging = drag?.id === pin.marker.id && drag.role === pin.role}
    <button
      type="button"
      class="pin {pin.role}"
      class:editing={markersEditing}
      class:incomplete={!pin.marker.source || !pin.marker.target}
      class:highlight={highlightId === pin.marker.id}
      style:left="{dragging ? drag!.x : pin.x}%"
      style:top="{dragging ? drag!.y : pin.y}%"
      aria-label="{pin.marker.label}: {pin.role === 'source' ? 'Sprung' : 'Ziel'}"
      onpointerdown={(e) => pinDown(e, pin)}
      onpointermove={pinMove}
      onpointerup={(e) => pinUp(e, pin)}
      onclick={(e) => e.stopPropagation()}
    >
      <span class="glyph">{pin.role === "source" ? "↩" : "★"}</span>
      <span class="name">{pin.marker.label}</span>
    </button>
  {/each}
  <span class="number" aria-hidden="true">{pageNumber}</span>
</div>

<style>
  .page {
    position: relative;
    flex: 0 0 auto;
    /* Platzhalter bis zum Rendern – weiß wie die Seite selbst, kein Farbwechsel beim Laden */
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.55);
    overflow: hidden;
    contain: layout paint;
  }
  .placing {
    cursor: crosshair;
    outline: 3px dashed var(--accent);
    outline-offset: 6px;
    overflow: visible;
  }
  canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }
  .pdf {
    opacity: 0;
    transition: opacity var(--dur-base) var(--ease-out);
  }
  .pdf.ready {
    opacity: 1;
  }
  .loading {
    position: absolute;
    inset: 0;
    background: linear-gradient(100deg, transparent 30%, rgba(0, 0, 0, 0.05) 50%, transparent 70%) 0 0 / 200%
      100%;
    animation: shimmer 1.4s linear infinite;
  }
  @keyframes shimmer {
    to {
      background-position: -200% 0;
    }
  }
  .ink {
    pointer-events: none;
    touch-action: auto;
  }
  .ink.active {
    pointer-events: auto;
    /* Pinch-Zoom übernimmt der Viewer selbst */
    touch-action: pan-x pan-y;
    cursor: crosshair;
  }
  .ink.active.free {
    touch-action: none;
  }
  .pin {
    position: absolute;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px 0 8px;
    border-radius: var(--r-pill);
    transform: translate(-50%, -50%);
    font-weight: 800;
    font-size: 12px;
    white-space: nowrap;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
    animation: pop var(--dur-sheet) var(--ease-pop) both;
    transition:
      transform var(--dur-base) var(--ease-spring),
      box-shadow var(--dur-base);
  }
  .pin.source {
    background: var(--accent);
    color: var(--on-accent);
  }
  .pin.target {
    background: var(--accent-2);
    color: #2a0d04;
  }
  .pin.incomplete {
    opacity: 0.75;
    outline: 2px dashed currentColor;
  }
  .pin.editing {
    cursor: grab;
    touch-action: none;
  }
  .pin:active {
    transform: translate(-50%, -50%) scale(0.94);
  }
  .pin.highlight {
    animation: beacon 1.6s var(--ease-out);
  }
  /* eigene Klasse – die globale „.label“ (grau, Versalien) würde den Kontrast ruinieren */
  .name {
    color: inherit;
    font-size: 13px;
    letter-spacing: 0.01em;
  }
  .glyph {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.18);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
  }
  @keyframes pop {
    from {
      transform: translate(-50%, -50%) scale(0.4);
      opacity: 0;
    }
  }
  @keyframes beacon {
    0%,
    40%,
    80% {
      box-shadow:
        0 6px 18px rgba(0, 0, 0, 0.35),
        0 0 0 0 rgba(77, 141, 255, 0.7);
    }
    20%,
    60% {
      box-shadow:
        0 6px 18px rgba(0, 0, 0, 0.35),
        0 0 0 14px rgba(77, 141, 255, 0);
    }
  }
  .number {
    position: absolute;
    right: 10px;
    bottom: 8px;
    font-size: 11px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.28);
    pointer-events: none;
  }
</style>
